import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuestService } from '../../services/quest.service';

@Component({
  selector: 'app-assign-quests',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './assign-quests.component.html',
  styleUrl: './assign-quests.component.css'
})
export class AssignQuestsComponent implements OnInit {
  allQuests: any[] = [];
  filteredQuests: any[] = [];
  selectedQuestIds: Set<string> = new Set();
  
  searchQuery: string = '';
  filterDate: string = '';

  // Modal state
  showAssignModal: boolean = false;
  members: any[] = [];
  filteredMembers: any[] = [];
  selectedMemberIds: Set<string> = new Set();
  memberSearchName: string = '';
  memberSearchEmail: string = '';
  isLoadingMembers: boolean = false;

  // Success modal state
  showSuccessModal: boolean = false;
  assignedMembersCount: number = 0;

  // Warning card state
  showWarningCard: boolean = false;
  warningMember: any = null;
  overlappingQuests: any[] = [];

  constructor(private questService: QuestService) {}

  ngOnInit(): void {
    this.loadQuests();
  }

  private loadQuests(): void {
    this.questService.getQuests().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.result || res?.items || []);
        this.allQuests = [...list].sort((a: any, b: any) => {
          const da = new Date(a.createdDateTime || a.createdDate || 0).getTime();
          const db = new Date(b.createdDateTime || b.createdDate || 0).getTime();
          return db - da;
        });
        this.filteredQuests = [...this.allQuests];
      },
      error: (err: any) => {
        console.error('Error loading quests list', err);
      },
    });
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onDateFilterChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.allQuests];

    // Filter by search query (quest name)
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      const query = this.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(quest => 
        (quest.name || '').toLowerCase().includes(query)
      );
    }

    // Filter by date
    if (this.filterDate) {
      filtered = filtered.filter(quest => {
        const questDate = new Date(quest.createdDateTime || quest.createdDate || '');
        const filterDate = new Date(this.filterDate);
        return questDate.toDateString() === filterDate.toDateString();
      });
    }

    this.filteredQuests = filtered;
  }

  toggleQuestSelection(questId: string): void {
    if (this.selectedQuestIds.has(questId)) {
      this.selectedQuestIds.delete(questId);
    } else {
      this.selectedQuestIds.add(questId);
    }
  }

  isQuestSelected(questId: string): boolean {
    return this.selectedQuestIds.has(questId);
  }

  hasSelectedQuests(): boolean {
    return this.selectedQuestIds.size > 0;
  }

  onAssignQuestsTo(): void {
    this.loadMembers();
  }

  loadMembers(): void {
    this.isLoadingMembers = true;
    this.showAssignModal = true;
    this.questService.getMemberQuestInfo().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.result || res?.items || []);
        this.members = list;
        this.filteredMembers = [...this.members];
        this.isLoadingMembers = false;
      },
      error: (err: any) => {
        console.error('Error loading members', err);
        this.members = [];
        this.filteredMembers = [];
        this.isLoadingMembers = false;
      },
    });
  }

  closeAssignModal(): void {
    this.showAssignModal = false;
    this.selectedMemberIds.clear();
    this.memberSearchName = '';
    this.memberSearchEmail = '';
    this.filteredMembers = [...this.members];
  }

  onMemberSearchChange(): void {
    this.applyMemberFilters();
  }

  applyMemberFilters(): void {
    let filtered = [...this.members];

    // Filter by name
    if (this.memberSearchName && this.memberSearchName.trim() !== '') {
      const query = this.memberSearchName.toLowerCase().trim();
      filtered = filtered.filter(member => 
        (member.name || '').toLowerCase().includes(query)
      );
    }

    // Filter by email
    if (this.memberSearchEmail && this.memberSearchEmail.trim() !== '') {
      const query = this.memberSearchEmail.toLowerCase().trim();
      filtered = filtered.filter(member => 
        (member.email || '').toLowerCase().includes(query)
      );
    }

    this.filteredMembers = filtered;
  }

  toggleMemberSelection(event: Event, memberId: string): void {
    if (this.selectedMemberIds.has(memberId)) {
      // Deselecting - no need to check
      this.selectedMemberIds.delete(memberId);
      this.hideWarningCard();
    } else {
      // Check if any selected quests are already assigned to this member
      const member = this.members.find(m => m.memberId === memberId);
      if (member && member.questIds && Array.isArray(member.questIds)) {
        const selectedQuestIdsArray = Array.from(this.selectedQuestIds);
        const overlapping = member.questIds.filter((existingQuest: any) => 
          selectedQuestIdsArray.includes(existingQuest.questId)
        );

        if (overlapping.length > 0) {
          // Prevent checkbox from being checked
          event.preventDefault();
          event.stopPropagation();
          // Show warning card - don't select the member
          this.warningMember = member;
          this.overlappingQuests = overlapping;
          this.showWarningCard = true;
          // Explicitly ensure the member is not selected
          this.selectedMemberIds.delete(memberId);
          return; // Don't select the member
        }
      }
      // No overlap, proceed with selection
      this.selectedMemberIds.add(memberId);
      this.hideWarningCard();
    }
  }

  hideWarningCard(): void {
    this.showWarningCard = false;
    // Ensure the member is not selected when warning is closed
    if (this.warningMember) {
      this.selectedMemberIds.delete(this.warningMember.memberId);
    }
    this.warningMember = null;
    this.overlappingQuests = [];
  }

  cancelSelection(): void {
    // Remove selection and close warning
    if (this.warningMember) {
      this.selectedMemberIds.delete(this.warningMember.memberId);
    }
    this.hideWarningCard();
  }

  isMemberSelected(memberId: string): boolean {
    return this.selectedMemberIds.has(memberId);
  }

  onAssignQuests(): void {
    const selectedMemberIds = Array.from(this.selectedMemberIds);
    const selectedQuestIds = Array.from(this.selectedQuestIds);

    if (selectedMemberIds.length === 0 || selectedQuestIds.length === 0) {
      return;
    }

    // Build payload in the specified format
    // Format: Array of objects, each with memberId and questIds array
    const payload: any[] = [];
    selectedMemberIds.forEach(memberId => {
      const memberPayload: any = {
        memberId: memberId,
        questIds: [...selectedQuestIds] // Array of quest IDs
      };
      payload.push(memberPayload);
    });

    // Call API to assign quests
    this.questService.assignQuestsToMembers(payload).subscribe({
      next: (res: any) => {
        console.log('Quests assigned successfully', res);
        this.assignedMembersCount = selectedMemberIds.length;
        this.closeAssignModal();
        this.showSuccessModal = true;
        // Clear selections after successful assignment
        this.selectedQuestIds.clear();
        this.selectedMemberIds.clear();
      },
      error: (err: any) => {
        console.error('Error assigning quests', err);
        alert('Failed to assign quests. Please try again.');
      },
    });
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
    this.assignedMembersCount = 0;
  }
}

